import React, { useCallback, useEffect, useRef, useState } from "react";
import tw from "twin.macro";
import styled, { css } from "styled-components";

const TabsBar = styled.nav`
    ${tw`w-full`}
    display: flex;
    flex-wrap: wrap; /* ✅ 允许换行 */
    justify-content: center;
    align-items: center;
    gap: 13px;
    padding: 0px 0px 7px;
    font-family: "Roboto Condensed", sans-serif;
    background-color: #fffbdc;

    @media (min-width: 768px) {
        flex-wrap: nowrap; /* ✅ 平板及以上不换行 */
        padding: 0px 40px 16px;
        gap: 28px;
    }
`;

const TabBtn = styled.button`
    ${tw`relative font-semibold`}
    flex: 1 1 45%; /* ✅ 手机端两个一行 */
    max-width: 48%;
    background: transparent;
    border: 0;
    cursor: pointer;
    outline: none;
    padding: 8px 2px 12px;
    font-size: clamp(1.34rem, 1.26rem + 0.19vw, 1.6rem);
    line-height: 1;
    color: ${(p) => (p.$active ? "#14133B" : "#9CA3AF")};
    transition: color 0.12s ease;
    text-align: center;

    & > span {
        font-weight: 400;
        display: inline-block;
        transform-origin: center;
    }

    &::after {
        content: "";
        position: absolute;
        left: 50%;
        bottom: 2px;
        width: ${(p) => p.$dotSize ?? 8}px;
        height: ${(p) => p.$dotSize ?? 8}px;
        background: #14133b;
        border-radius: 50%;
        transform: translateX(-50%) scale(${(p) => (p.$active ? 1 : 0)});
        opacity: ${(p) => (p.$active ? 1 : 0)};
        transition: transform 0.18s ease, opacity 0.18s ease;
    }

    &:hover::after {
        transform: translateX(-50%) scale(1);
        opacity: ${(p) => (p.$active ? 1 : 0.5)};
    }

    @media (min-width: 768px) {
        flex: none;
        max-width: none;
        padding: 10px 6px 18px;
        font-size: clamp(1.98rem, 1.92rem + 0.2vw, 2.12rem);
        &::after {
            bottom: 4px;
        }
    }
`;


const PageWrap = tw.div`w-full bg-white text-dark-500`;
const SectionSpacer = tw.div`mt-8`;

const SectionContainer = styled.section(({ $bgColor }) => [
    tw`relative -mx-8 -mt-8 font-work_sans`,
    css`
        background-color: ${$bgColor};
    `,
]);

const SectionInner = styled.div(({ $reverse }) => [
    tw`flex flex-col-reverse lg:flex-row text-gray-900 min-h-screen`,
    $reverse &&
        css`
            @media (min-width: 1024px) {
                flex-direction: row-reverse;
            }
        `,
]);

const LeftColumn = styled.div`
    ${tw`px-4 lg:px-6 xl:px-8 lg:w-1/2 flex flex-col justify-center lg:min-h-0`}
    padding-top: 2.1rem;
    padding-bottom: 2.1rem;

    @media (min-width: 768px) {
        padding-top: 2rem;
        padding-bottom: 3rem;
    }
`;

const Content = styled.div`
    ${tw`flex flex-col items-start text-left`}

    @media (min-width: 1024px) {
        margin-top: 1rem;
        margin-bottom: 1.5rem;
    }
`;

const Heading = styled.h2`
    ${tw`tracking-tight`}
    font-size: 3rem;
    line-height: 0.8;
    padding-bottom: 14px;
    @media (min-width: 768px) {
        font-size: clamp(3rem, 2.8rem + 1.76vw, 6.6rem);
        line-height: 0.98;
    }
`;

const Paragraph = styled.p`
    margin-top: 1.05rem;
    margin-bottom: 1.05rem;
    max-width: 42rem;
    font-family: "Roboto Condensed", sans-serif;
    font-weight: 200;
    font-size: 3rem;
    line-height: 1;

    @media (min-width: 1024px) {
        margin-top: 2rem;
        font-size: clamp(4rem, 4rem + 1.814vw, 6rem);
        margin-bottom: 2rem;
    }
`;

const MediaColumn = styled.div`
    ${tw`w-full`}
    position: relative;
    flex: 1 1 auto;
    width: 100%;
    min-height: 50vh;
    @media (min-width: 1024px) {
        flex: none;
        width: 50%;
        min-height: 60vh;
    }
    background: #0b1f3a;
`;

const MediaImage = styled.img`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    pointer-events: none;
`;

const InfoBar = styled.div`
    ${tw`w-full`}
    background-color: #fffbdc;
`;

const InfoBarInner = styled.div`
    ${tw`max-w-5xl mx-auto`}
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    color: #14133b;
    padding-top: calc(clamp(1.925rem, 1.61rem + 0.98vw, 2.625rem) * 2);
    padding-bottom: calc(clamp(1.925rem, 1.61rem + 0.98vw, 2.625rem) * 1.5);
    padding-left: clamp(1.75rem, 1.4rem + 1vw, 3rem);
    padding-right: clamp(1.75rem, 1.4rem + 1vw, 3rem);
`;

const InfoTitle = styled.h3`
    ${tw`text-2xl sm:text-3xl`}
    
    line-height: 1.1;
    font-family: "Roboto Condensed", sans-serif;
    font-weight: 400;
    margin: 0;
`;

const InfoDescription = styled.p`
    ${tw`text-base sm:text-lg leading-relaxed`}
    font-family: "Work Sans", sans-serif;
    margin: 0;
    padding-bottom: clamp(1.925rem, 1.61rem + 0.98vw, 2.625rem);
    padding-left: 15%;
`;

const SECTION_ITEMS = [
    {
        id: "foundation",
        title: "Foundation",
        description:
            "Nourishing people, communities, and the planet, one meal at a time",
        background: "#fdd96e",
        imagePlacement: "right",
        image:
            "https://uk-grain.oss-eu-west-1.aliyuncs.com/foundation/Nourishing%20people.jpg",
        infoTitle:
            "At Grain, we believe good food can change lives. That’s why the Grain Foundation is dedicated to giving back, supporting healthier futures at home and fighting hunger and malnutrition abroad.",
        infoDescription:
            "From classrooms in the UK to communities across Africa, we’re committed to helping people eat better, live longer, and thrive together.",
    },
    {
        id: "schools",
        title: "Schools",
        description: "Growing healthy habits from the ground up",
        background: "#d9e1a2",
        imagePlacement: "left",
        image:
            "https://uk-grain.oss-eu-west-1.aliyuncs.com/foundation/Healthy%20Eating%20for%20Schools.png",
        infoTitle:
            "Every child deserves to understand what “good food” really means. Through the Grain Foundation Schools Programme, we provide free educational materials and digital resources to help teachers bring healthy eating to life.",
        infoDescription:
            "Our mission is simple: to give young people the knowledge to make better choices as they grow — understanding not just what tastes good, but what does good. We aim to reach thousands of schools across the UK, empowering the next generation to eat well, think clearly, and live fully.",
    },
    {
        id: "communities",
        title: "Communities",
        description: "Because nourishment should never depend on where you’re born",
        background: "#b2cac7",
        imagePlacement: "right",
        image:
            "https://uk-grain.oss-eu-west-1.aliyuncs.com/foundation/Water%20%26%20Food%20for%20Communities.png",
        infoTitle:
            "Across Africa and other regions where the need is greatest, the Grain Foundation partners with local and international organisations to deliver clean water and food support to vulnerable communities.",
        infoDescription:
            "Every project we support shares one belief: that access to safe water and nutritious food is not a privilege — it’s a right. Together, we’re building hope, resilience, and healthier lives, one community at a time.",
    },
    {
        id: "people",
        title: "Our Promise",
        description: "Giving time to give back",
        background: "#f5dfd0",
        imagePlacement: "left",
        image:
            "https://uk-grain.oss-eu-west-1.aliyuncs.com/foundation/Giving%20time%20to%20give%20back.png",
        infoTitle:
            "Every member of the Grain team is given two paid days each year to volunteer in local community projects, from school initiatives to food support drives.",
        infoDescription:
            "We believe caring for the communities around our stores is just as important as the food we serve. Because when we grow together, we thrive together.",
    },
];
const OurStoryComponent = () => {
    const [activeTab, setActiveTab] = useState(SECTION_ITEMS[0].id);
    const sectionRefs = useRef({});

    const scrollToSection = useCallback((id, behavior = "smooth") => {
        if (!id) return;
        setActiveTab(id);
        const target = sectionRefs.current[id];
        if (target) {
            target.scrollIntoView({ behavior, block: "start" });
        }
    }, []);

    const handleTabClick = useCallback(
        (id) => {
            scrollToSection(id, "smooth");
        },
        [scrollToSection]
    );

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                const intersecting = entries
                    .filter((entry) => entry.isIntersecting)
                    .sort(
                        (a, b) =>
                            a.target.getBoundingClientRect().top -
                            b.target.getBoundingClientRect().top
                    );

                const nextEntry = intersecting[0];
                if (nextEntry?.target?.dataset?.sectionId) {
                    setActiveTab((prev) =>
                        prev === nextEntry.target.dataset.sectionId
                            ? prev
                            : nextEntry.target.dataset.sectionId
                    );
                }
            },
            {
                threshold: [0, 0.25, 0.5, 0.75],
                rootMargin: "-10% 0px -45% 0px",
            }
        );

        SECTION_ITEMS.forEach(({ id }) => {
            const node = sectionRefs.current[id];
            if (node) {
                observer.observe(node);
            }
        });

        const handleScrollTop = () => {
            if (window.scrollY <= 5) {
                setActiveTab((prev) =>
                    prev === SECTION_ITEMS[0].id ? prev : SECTION_ITEMS[0].id
                );
            }
        };

        window.addEventListener("scroll", handleScrollTop, { passive: true });

        return () => {
            observer.disconnect();
            window.removeEventListener("scroll", handleScrollTop);
        };
    }, []);

    useEffect(() => {
        const handleHashNavigation = () => {
            const hash = window.location.hash.replace("#", "");
            if (hash && sectionRefs.current[hash]) {
                scrollToSection(hash, "smooth");
            }
        };

        handleHashNavigation();
        window.addEventListener("hashchange", handleHashNavigation);

        return () => {
            window.removeEventListener("hashchange", handleHashNavigation);
        };
    }, [scrollToSection]);
    const tabsDotSize = 8;

    return (
        <PageWrap>
            <TabsBar>
                {SECTION_ITEMS.map(({ id, title }) => (
                    <TabBtn
                        key={id}
                        type="button"
                        onClick={() => handleTabClick(id)}
                        $active={activeTab === id}
                        $dotSize={tabsDotSize}
                    >
                        <span>{title}</span>
                    </TabBtn>
                ))}
            </TabsBar>

            <SectionSpacer />

            {SECTION_ITEMS.map(
                ({
                     id,
                     title,
                     description,
                     background,
                     imagePlacement,
                     image,
                     infoTitle,
                     infoDescription,
                 }) => (
                    <SectionContainer
                        key={id}
                        id={id}
                        data-section-id={id}
                        ref={(node) => {
                            if (node) {
                                sectionRefs.current[id] = node;
                            }
                        }}
                        $bgColor={background}
                    >
                        <SectionInner $reverse={imagePlacement === "left"}>
                            <LeftColumn>
                                <Content>
                                    <Heading>{title}</Heading>
                                    <Paragraph>{description}</Paragraph>
                                </Content>
                            </LeftColumn>

                            <MediaColumn aria-hidden="true">
                                <MediaImage src={image} alt="" loading="lazy" />
                            </MediaColumn>
                        </SectionInner>

                        <InfoBar>
                            <InfoBarInner>
                                <InfoTitle>{infoTitle}</InfoTitle>
                                <InfoDescription>{infoDescription}</InfoDescription>
                            </InfoBarInner>
                        </InfoBar>
                    </SectionContainer>
                )
            )}
        </PageWrap>
    );
};

export default OurStoryComponent;
